import { Coords } from "tm-shared/types";

export const getLocation = (
    opts: Partial<PositionOptions> = {},
): Promise<Coords> => {
    const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 6_000,
        maximumAge: 0,
        ...opts,
    };

    const HARD_TIMEOUT_MS = (options.timeout ?? 0) + 2_000;

    return new Promise((resolve, reject) => {
        if (!("geolocation" in navigator)) {
            reject(new Error("Geolocation не підтримується в браузері"));
            return;
        }

        let settled = false;

        const hardTimer = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error("Таймаут геолокації"));
        }, HARD_TIMEOUT_MS);

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                if (settled) return;
                settled = true;
                window.clearTimeout(hardTimer);

                const { latitude, longitude, accuracy } = pos.coords;
                resolve({
                    lat: latitude,
                    lng: longitude,
                    accuracy: Math.round(accuracy),
                });
            },
            (err) => {
                if (settled) return;
                settled = true;
                window.clearTimeout(hardTimer);
                reject(err);
            },
            options,
        );
    });
};

export const getPreciseLocation = async (
    attempts = 3,
    acceptableAccuracy = 50,
): Promise<Coords> => {
    const TOTAL_BUDGET_MS = 20_000;
    const MIN_ATTEMPT_TIMEOUT = 1_000;
    const MAX_ATTEMPT_TIMEOUT = 15_000;
    const TIME_BUFFER = 250;

    const deadline = Date.now() + TOTAL_BUDGET_MS;

    const measurements: Coords[] = [];
    let lastError: unknown = null;

    for (let i = 0; i < attempts; i++) {
        const timeLeft = deadline - Date.now();

        if (timeLeft <= TIME_BUFFER) break;

        try {
            const perAttemptTimeout = Math.max(
                MIN_ATTEMPT_TIMEOUT,
                Math.min(MAX_ATTEMPT_TIMEOUT, timeLeft - TIME_BUFFER),
            );

            const location = await getLocation({ timeout: perAttemptTimeout });
            measurements.push(location);

            if (location.accuracy <= acceptableAccuracy) {
                return location;
            }
        } catch (error) {
            lastError = error;

            if (error instanceof GeolocationPositionError) {
                if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
                    throw error;
                }
            }
        }
    }

    if (measurements.length > 0) {
        return measurements.reduce((best, current) =>
            current.accuracy < best.accuracy ? current : best,
        );
    }

    throw (
        lastError ??
        new Error("Не вдалося отримати вашу геолокацію. Спробуйте ще раз.")
    );
};

export const parsePointCoords = (
    geo?: string | null,
): [number, number] | null => {
    if (!geo) return null;

    const [latStr, lngStr] = geo.split(",").map((v) => v.trim());

    const lat = Number(latStr);
    const lng = Number(lngStr);

    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

    return [lng, lat];
};
